import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { serializeJsonLd } from "@stanza/shared/seo/json-ld";

/** Canonical production origin used to build absolute canonical + OG URLs. */
export const SITE_URL = "https://stanzacal.com";

/** Default social-share card (1200x630) served from the app's public folder. */
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * Centralized SEO meta for a route: title, description, canonical link, and
 * Open Graph + Twitter Card tags. Every marketing-relevant route should render
 * exactly one <Seo /> so per-page SERP listings and social previews stay
 * consistent and never drift apart. The canonical URL is derived from the
 * current pathname unless the route passes its own.
 *
 * @param {object} props
 * @param {string} props.title - Full <title> for the route.
 * @param {string} props.description - Meta + OG/Twitter description (~150-160 chars).
 * @param {string} [props.image] - Absolute OG/Twitter image URL. Defaults to the brand card.
 * @param {"website"|"article"|"profile"} [props.type] - og:type. Defaults to "website".
 * @param {string} [props.canonical] - Absolute canonical URL. Defaults to the current path.
 * @param {boolean} [props.noindex] - When true, emits robots noindex,nofollow.
 * @param {Array<object>} [props.jsonLd] - Structured-data objects, each emitted
 *   as a <script type="application/ld+json"> block.
 * @returns {JSX.Element}
 */
const Seo = ({
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  canonical: canonicalUrl,
  noindex = false,
  jsonLd = [],
}) => {
  const { pathname } = useLocation();
  const canonical = canonicalUrl || `${SITE_URL}${pathname}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Stanza" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {/* Escaped so a literal "</script>" in user content (reviews) can't break out of the tag; helmet assigns script children via innerHTML. */}
          {serializeJsonLd(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
